/**
 * Dexie database schema for the offline-first PWA.
 *
 * Tables mirror the Firestore collection structure. Since each browser
 * instance is scoped to a single user / company / financial year, we collapse
 * the `/companies/{companyId}/...` prefix into flat tables (the companyId is
 * still a column on each row in case multi-company sync is added later).
 *
 * Version history is critical with Dexie — increment the version number and
 * add a new .stores() call when changing the schema. Never edit a past
 * version's .stores() definition (users with old DBs will fail to upgrade).
 *
 * Index syntax (Dexie):
 *   '++id, fieldA, fieldB, [fieldA+fieldB], &uniqueField, *multiValueField'
 *
 * Prefixes:
 *   ++   = auto-increment primary key
 *   &    = unique index
 *   *    = multi-value (array) index
 *   [a+b]= compound index
 *
 * STATUS: Defined but inert (Phase 0). No code reads or writes these tables
 * yet. Implementation lands in Phase 2 when DexieStore is wired up.
 */

import Dexie from 'dexie';

export const dexieDb = new Dexie('amalerp');

dexieDb.version(1).stores({
  // ─── Transactions (the core ledger) ─────────────────────────────────
  // Indexes match the WHERE patterns in transactionHelper.getTransactions.
  transactions:
    'id, tranTypeCode, docDate, status, partyId, financialYearId, branchId, ' +
    '[tranTypeCode+financialYearId], [tranTypeCode+status], [partyId+status]',

  sub_transactions:
    'id, transactionId, accountId, status, financialYearId, ' +
    '[transactionId+accountId], [accountId+financialYearId+status]',

  stock_flow:
    'id, transactionId, itemId, locationId, branchId, status, ' +
    '[itemId+locationId], [itemId+status]',

  // ─── Document numbering ─────────────────────────────────────────────
  // Per Decision 1: pre-allocated ranges per device.
  document_counters:
    'id, tranTypeCode, branchId, financialYearId',

  // ─── Master data ─────────────────────────────────────────────────────
  accounts:        'id, code, level, parentId, isActive, linkedEntityType, linkedEntityId',
  customers:       'id, companyName, code, isActive',
  vendors:         'id, companyName, code, isActive',
  inventory_items: 'id, itemCode, itemName, categoryId, isActive, itemType',
  inventory_locations: 'id, locationName, isActive',
  inventory_categories: 'id, categoryName',
  inventory_sub_categories: 'id, subCategoryName, categoryId',
  units:           'id, unitName, isActive',
  banks:           'id, bankName, linkedAccountId, isDefault, isActive',
  employees:       'id, employeeCode, firstName, lastName, isActive, isSalesman',
  jobs:            'id, jobCode, jobName, isActive',
  regions:         'id, regionName, isActive',
  areas:           'id, areaName, regionId, isActive',
  branches:        'id, branchName, isActive, isDefault',
  financial_years: 'id, yearCode, isActive, startDate',

  // ─── Per-company config ─────────────────────────────────────────────
  // The active company doc itself (single row, keyed by company id).
  company:         'id, companyName',
  roles:           'id, roleName',
  company_users:   'id, roleId, isActive',

  // ─── Sync infrastructure (offline-first) ────────────────────────────
  // Outbox: every local write also lands here; sync worker drains it.
  sync_outbox:
    '++seq, status, targetPath, createdAt, lastAttemptAt, retries',

  // sync_state: per-collection last-successful-pull timestamp.
  sync_state:
    'collectionName, lastPulledAt',

  // ─── Attachments (held locally until sync uploads to Firebase Storage) ──
  attachments_pending:
    '++id, transactionId, fileName, mimeType, size, createdAt'
});

/**
 * Open the database explicitly (Dexie does this lazily on first use, but
 * opening up front lets us catch schema/upgrade errors at app start).
 *
 * STATUS: Not called yet in Phase 0. Will be invoked from app boot in Phase 2.
 */
export const openDexie = async () => {
  try {
    await dexieDb.open();
    return { ok: true };
  } catch (err) {
    console.error('[dexie] Failed to open local database:', err);
    return { ok: false, error: err };
  }
};

/**
 * Total disk space used by the local DB (rough — for the "storage health" UI).
 */
export const estimateDexieUsage = async () => {
  if (!navigator?.storage?.estimate) return null;
  const est = await navigator.storage.estimate();
  return {
    usedBytes: est.usage || 0,
    quotaBytes: est.quota || 0,
    percentUsed: est.quota ? Math.round(((est.usage || 0) / est.quota) * 100) : 0
  };
};
